import { useMemo } from 'react';
import { buildChildrenByValue, filterNestedGroups } from '../utils/tree';
import type { DropdownValue, FilterItems, MatchesItem, NestedGroupItem } from '../types';

type UseDropdownVisibilityOptions = {
  items: NestedGroupItem[];
  searchTerm: string;
  searchable: boolean;
  hideSearchParents?: boolean;
  expandable: boolean;
  expandedSet: Set<DropdownValue>;
  filterItems?: FilterItems;
  matchesItem?: MatchesItem;
};

export function useDropdownVisibility({
  items,
  searchTerm,
  searchable,
  hideSearchParents = false,
  expandable,
  expandedSet,
  filterItems,
  matchesItem,
}: UseDropdownVisibilityOptions) {
  const normalizedSearch = searchable ? searchTerm.trim() : '';

  const filteredTree = useMemo(() => {
    let filteredItems = items;

    if (normalizedSearch.length > 0) {
      filteredItems = filterItems
        ? filterItems(items, normalizedSearch)
        : filterNestedGroups(items, normalizedSearch, hideSearchParents, matchesItem);

      if (filterItems && matchesItem && hideSearchParents) {
        filteredItems = filterNestedGroups(filteredItems, normalizedSearch, true, matchesItem);
      }
    }

    return filteredItems;
  }, [filterItems, hideSearchParents, items, matchesItem, normalizedSearch]);
  const childrenByValue = useMemo(() => buildChildrenByValue(filteredTree), [filteredTree]);
  const visibleTree = useMemo(
    () => (expandable ? pruneCollapsedChildren(filteredTree, expandedSet) : filteredTree),
    [expandable, expandedSet, filteredTree],
  );

  return { visibleTree, childrenByValue };
}

function pruneCollapsedChildren(
  items: NestedGroupItem[],
  expandedSet: Set<DropdownValue>,
): NestedGroupItem[] {
  return items.map((item) => {
    if (item.children.length === 0) {
      return item;
    }

    return {
      ...item,
      children: expandedSet.has(item.value)
        ? pruneCollapsedChildren(item.children, expandedSet)
        : [],
    };
  });
}
